/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useState } from "react";

import { useRouter } from "next/router";
import styled from "styled-components";

import { ConditionalWrapper } from "src/components/UI/ConditionalWrapper/ConditionalWrapper";
import { NavArrow } from "src/components/UI/Icons";
import { Tooltip } from "src/components/UI/Tooltip";

import { TopbarItem } from "./AppTopbar";

export function NavButtons() {
  const router = useRouter();

  // historyIndex is where you are in the history (1 to history.length)
  // This value is stored in localStorage to allow history to persist over refreshes
  const [historyIndex, setHistoryIndex] = useState(
    history.length > 1
      ? parseInt(window.localStorage.getItem("historyIndex") || `${history.length}`)
      : history.length
  );

  const [[backDisabled, forwardDisabled], setNavDisabled] = useState([true, true]); // state of the back and forward buttons
  const [[back, forward], setNavButtons] = useState([false, false]); // true if the nav button has just been clicked
  const [key, setKey] = useState(window.history.state.key); // Differentiates between a push and a replace

  useEffect(() => {
    // Runs whenever the path of the current page changes:
    if (back) {
      // handle if the route has changed due to user hitting back button:

      // Back button should be disabled if the user is on the first page but not on a plate
      // Forward button should be disabled if at the end of history
      setNavDisabled([historyIndex === 1 && !router.asPath.includes("/plate/"), historyIndex === history.length]);

      // reset the click states to false
      setNavButtons([false, false]);

      // Store the new history index in localStorage
      window.localStorage.setItem("historyIndex", `${historyIndex}`);
    } else if (forward) {
      // handle if the route has changed due to user hitting forward button:

      // Back button will always be enabled
      // Forward button should be disabled if at the end of history
      setNavDisabled([false, historyIndex === history.length]);

      // reset the click states to false
      setNavButtons([false, false]);

      // Store the new history index in localStorage
      window.localStorage.setItem("historyIndex", `${historyIndex}`);
    } else if (key === window.history.state.key) {
      // If the key has not changed, the path has been replaced rather than pushed, so the history index will be the same as the last time

      // Back button disabled if at the beginning of history
      // Forward button disabled if at the end of history
      setNavDisabled([historyIndex === 1, historyIndex === history.length]);
    } else {
      // If a path has been pushed, then the user has navigated to a new page:

      // Back button will alwaty be enabled
      // The user must be at the end of their history, so the forward button should be disabled
      setNavDisabled([false, true]);

      // User at the end of history, so their index is the length of history
      setHistoryIndex(history.length);

      // Store the new history index in localStorage
      window.localStorage.setItem("historyIndex", `${history.length}`);
    }
    setKey(window.history.state.key);
  }, [router.asPath]);

  useEffect(() => {
    // runs when the page refreshes:

    // Back disabled when the user's history index is 1 and they are not on a plate
    // Forward disabled when they are at the end of history
    setNavDisabled([!router.asPath.includes("/plate/") && historyIndex === 1, historyIndex === history.length]);

    // useEffect above also runs on refresh, so this overwrites the historyIndex to the stored value
    window.localStorage.setItem("historyIndex", `${historyIndex}`);
    setHistoryIndex(historyIndex);
  }, []);

  const goBack = () => {
    // Runs when the user clicks the back button:
    if (historyIndex > 1) {
      // if there is history to go back to:
      setHistoryIndex(historyIndex - 1); // decrement history index
      router.back(); // go back in history
      setNavButtons([true, false]); // set back to true
    } else if (router.asPath.includes("/plate/")) {
      // no history but on plate case:
      router.replace("/home/mine"); // go to home
      setNavButtons([true, false]); // set back to true
    }
  };

  const goForward = () => {
    // Runs when the user clicks the forward button:
    if (!forwardDisabled) {
      // if there is history to go forward to:
      window.history.forward(); // go forward in history
      setHistoryIndex(historyIndex + 1); // increment history index
      setNavButtons([false, true]); // set forward to true
    }
  };

  return (
    <NavButtonsWrapper>
      <ConditionalWrapper
        condition={!backDisabled}
        wrapper={(children: React.ReactNode) => (
          <Tooltip tooltipText={"Go back"} delayShow={1000}>
            {children}
          </Tooltip>
        )}
      >
        <NavButton onClick={goBack} onDoubleClick={(e) => e.stopPropagation()}>
          <TopbarItem disabled={backDisabled}>
            <NavArrow back style={{ position: "relative", top: "2px" }} />
          </TopbarItem>
        </NavButton>
      </ConditionalWrapper>
      <ConditionalWrapper
        condition={!forwardDisabled}
        wrapper={(children: React.ReactNode) => (
          <Tooltip tooltipText={"Go forward"} delayShow={1000}>
            {children}
          </Tooltip>
        )}
      >
        <NavButton onClick={goForward} onDoubleClick={(e) => e.stopPropagation()}>
          <TopbarItem disabled={forwardDisabled}>
            <NavArrow style={{ position: "relative", top: "2px" }} />
          </TopbarItem>
        </NavButton>
      </ConditionalWrapper>
    </NavButtonsWrapper>
  );
}

const NavButton = styled.div`
  height: 25px;
  width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 5px;
  transition: all 150ms ease;
  -webkit-app-region: no-drag;
`;

const NavButtonsWrapper = styled.div`
  margin-right: 5px;
  display: flex;
`;
